import { useMemo, useState } from 'react';
import {
  Card,
  Space,
  Segmented,
  DatePicker,
  Button,
  Spin,
  Empty,
  Select,
  Typography,
  Input,
} from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
  Legend,
} from 'recharts';
import { usePortalCtx } from '../context.js';
import AnalyticsList from '../analytics/AnalyticsList.js';
import { useConsentSeries, useExperimentSummary } from '../../analytics/api.js';

const { RangePicker } = DatePicker;

type RangeKey = '7d' | '30d' | '90d';

const RANGE_TO_OFFSET: Record<RangeKey, number> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
};

const formatIso = (value: Dayjs) => value.toISOString();

const chartContainerStyle = { width: '100%', height: 320 } as const;

function exportCsv(
  filename: string,
  headers: string[],
  rows: Array<Array<string | number | null>>,
) {
  const escape = (value: string | number | null): string => {
    if (value === null || value === undefined) return '';
    const str = String(value);
    if (str.includes(',') || str.includes('\n') || str.includes('"'))
      return `"${str.replace(/"/g, '""')}"`;
    return str;
  };
  const body = [headers.join(','), ...rows.map((row) => row.map(escape).join(','))].join('\n');
  const blob = new Blob([body], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function AnalyticsPage() {
  const { registryUrl, siteKey } = usePortalCtx();
  const [rangeKey, setRangeKey] = useState<RangeKey>('30d');
  const [from, setFrom] = useState<Dayjs>(() => dayjs().subtract(RANGE_TO_OFFSET[rangeKey], 'day'));
  const [to, setTo] = useState<Dayjs>(() => dayjs());
  const [bucket, setBucket] = useState<'day' | 'hour'>('day');
  const [region, setRegion] = useState<string>('');
  const [experimentFilter, setExperimentFilter] = useState<string | undefined>(undefined);

  const consentParams = useMemo(
    () => ({
      baseUrl: registryUrl,
      siteKey: siteKey ?? undefined,
      from: formatIso(from),
      to: formatIso(to),
      bucket,
      region: region.trim() || undefined,
    }),
    [registryUrl, siteKey, from, to, bucket, region],
  );

  const experimentsParams = useMemo(
    () => ({
      baseUrl: registryUrl,
      siteKey: siteKey ?? undefined,
      from: formatIso(from),
      to: formatIso(to),
      experiment: experimentFilter,
    }),
    [registryUrl, siteKey, from, to, experimentFilter],
  );

  const consentQuery = useConsentSeries(consentParams);
  const experimentsQuery = useExperimentSummary(experimentsParams);

  const consentData = useMemo(() => consentQuery.data ?? [], [consentQuery.data]);
  const experimentData = useMemo(() => experimentsQuery.data ?? [], [experimentsQuery.data]);
  const experimentChartData = useMemo(
    () =>
      experimentData.map((row) => ({
        ...row,
        acceptRatePct: Number((row.acceptRate * 100).toFixed(2)),
      })),
    [experimentData],
  );
  const experimentVariants = useMemo(
    () => Array.from(new Set(experimentData.map((row) => row.experiment))).filter(Boolean),
    [experimentData],
  );

  const handleRangeChange = (value: RangeKey) => {
    setRangeKey(value);
    setFrom(dayjs().subtract(RANGE_TO_OFFSET[value], 'day'));
    setTo(dayjs());
  };

  const handlePickerChange = (values: null | [Dayjs | null, Dayjs | null]) => {
    if (!values || !values[0] || !values[1]) return;
    setFrom(values[0]);
    setTo(values[1]);
  };

  const consentCsv = () => {
    if (!consentData.length) return;
    exportCsv(
      'consent-series.csv',
      ['ts', 'accept', 'adjust', 'reject'],
      consentData.map((p) => [p.ts, p.accept, p.adjust, p.reject]),
    );
  };

  const experimentsCsv = () => {
    if (!experimentData.length) return;
    exportCsv(
      'experiments-summary.csv',
      ['experiment', 'variant', 'sessions', 'acceptRate', 'p50DecisionMs', 'p95DecisionMs'],
      experimentData.map((row) => [
        row.experiment,
        row.variant,
        row.sessions,
        Number((row.acceptRate * 100).toFixed(2)),
        row.p50DecisionMs ?? '',
        row.p95DecisionMs ?? '',
      ]),
    );
  };

  const quickRange = (
    <Segmented<RangeKey>
      value={rangeKey}
      onChange={(value) => handleRangeChange(value as RangeKey)}
      options={[
        { label: '7 days', value: '7d' },
        { label: '30 days', value: '30d' },
        { label: '90 days', value: '90d' },
      ]}
    />
  );

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card
        title="Consent Funnel"
        extra={
          <Space size="small">
            {quickRange}
            <RangePicker
              allowClear={false}
              value={[from, to]}
              onChange={handlePickerChange}
              maxDate={dayjs()}
            />
            <Select
              value={bucket}
              style={{ width: 120 }}
              onChange={(value) => setBucket(value)}
              options={[
                { label: 'Day buckets', value: 'day' },
                { label: 'Hour buckets', value: 'hour' },
              ]}
            />
            <Input
              allowClear
              placeholder="Region code (optional)"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              style={{ width: 180 }}
            />
            <Button onClick={consentCsv} disabled={!consentData.length}>
              Download CSV
            </Button>
          </Space>
        }
      >
        {consentQuery.isLoading ? (
          <Spin />
        ) : consentData.length ? (
          <div style={chartContainerStyle}>
            <ResponsiveContainer>
              <AreaChart data={consentData} margin={{ top: 16, right: 16, bottom: 16, left: 0 }}>
                <defs>
                  <linearGradient id="acceptGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#52c41a" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#52c41a" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="adjustGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#faad14" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#faad14" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="rejectGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ff4d4f" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#ff4d4f" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="ts"
                  tickFormatter={(value) =>
                    dayjs(value).format(bucket === 'day' ? 'MMM D' : 'MMM D HH:mm')
                  }
                />
                <YAxis allowDecimals={false} />
                <Tooltip
                  labelFormatter={(value) => dayjs(value as string).format('YYYY-MM-DD HH:mm')}
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="accept"
                  name="Accept"
                  stroke="#389e0d"
                  fillOpacity={1}
                  fill="url(#acceptGradient)"
                />
                <Area
                  type="monotone"
                  dataKey="adjust"
                  name="Adjust"
                  stroke="#d48806"
                  fillOpacity={1}
                  fill="url(#adjustGradient)"
                />
                <Area
                  type="monotone"
                  dataKey="reject"
                  name="Reject"
                  stroke="#cf1322"
                  fillOpacity={1}
                  fill="url(#rejectGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : consentQuery.isError ? (
          <Typography.Text type="danger">Failed to load consent analytics.</Typography.Text>
        ) : (
          <Empty description="No consent data for the selected window" />
        )}
      </Card>

      <Card
        title="Experiment Performance"
        extra={
          <Space size="small">
            <Select
              allowClear
              placeholder="Filter experiment"
              style={{ minWidth: 180 }}
              value={experimentFilter}
              onChange={(value) => setExperimentFilter(value || undefined)}
              options={experimentVariants.map((exp) => ({ label: exp, value: exp }))}
            />
            <Button onClick={experimentsCsv} disabled={!experimentData.length}>
              Download CSV
            </Button>
          </Space>
        }
      >
        {experimentsQuery.isLoading ? (
          <Spin />
        ) : experimentChartData.length ? (
          <div style={chartContainerStyle}>
            <ResponsiveContainer>
              <BarChart
                data={experimentChartData}
                margin={{ top: 16, right: 16, bottom: 16, left: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="variant" />
                <YAxis yAxisId="left" orientation="left" allowDecimals={false} />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tickFormatter={(v) => `${Number(v).toFixed(0)}%`}
                />
                <Tooltip
                  formatter={(value: number, name) =>
                    name === 'acceptRatePct'
                      ? [`${Number(value).toFixed(2)}%`, 'Accept rate']
                      : [value, 'Sessions']
                  }
                />
                <Legend />
                <Bar yAxisId="left" dataKey="sessions" name="Sessions" fill="#1677ff" />
                <Bar yAxisId="right" dataKey="acceptRatePct" name="Accept rate" fill="#52c41a" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : experimentsQuery.isError ? (
          <Typography.Text type="danger">Failed to load experiment analytics.</Typography.Text>
        ) : (
          <Empty description="No experiment summary for the selected window" />
        )}
      </Card>

      <Card title="Latest Metrics">
        <AnalyticsList />
      </Card>
    </Space>
  );
}

export default AnalyticsPage;
